import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiDelete } from '@/lib/api';

// ============================================
// TYPES (t14)
// ============================================
//
// Mirrors backend/src/schemas/task-dependency.ts. Kept local so the
// frontend bundle doesn't depend on backend-side zod-generated
// types.

export type TaskDependencyType = 'FS' | 'SS' | 'FF' | 'SF';

export interface TaskDependency {
    id: string;
    type: TaskDependencyType;
    predecessorId: string;
    successorId: string;
    projectId: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateTaskDependencyInput {
    predecessorId: string;
    successorId: string;
    type?: TaskDependencyType;
}

// ============================================
// QUERY KEYS
// ============================================

export const taskDependencyKeys = {
    all: ['taskDependencies'] as const,
    lists: () => [...taskDependencyKeys.all, 'list'] as const,
    list: (projectId: string) => [...taskDependencyKeys.lists(), projectId] as const,
};

// ============================================
// HOOKS
// ============================================

export function useTaskDependencies(projectId: string | undefined) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, taskDependencyKeys.list(projectId || '')),
        queryFn: async () => {
            if (!projectId) return { dependencies: [], total: 0 };
            const data = await projectReaderGet<{ dependencies: TaskDependency[]; total: number }>(reader,
                `/projects/${projectId}/task-dependencies`
            );
            return data;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!projectId,
        staleTime: 30 * 1000, // 30 seconds — Gantt re-renders are expensive
    });
}

export function useCreateTaskDependency(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateTaskDependencyInput) => {
            return apiPost<TaskDependency>(`/projects/${projectId}/task-dependencies`, data);
        },
        // Optimistic add: append the placeholder immediately so the
        // Gantt arrow doesn't flash on a slow network. Rolled back on
        // error.
        onMutate: async (newDep) => {
            await queryClient.cancelQueries({ queryKey: taskDependencyKeys.list(projectId) });
            const previous = queryClient.getQueryData<{
                dependencies: TaskDependency[];
                total: number;
            }>(taskDependencyKeys.list(projectId));
            const placeholder: TaskDependency = {
                id: `temp_${Date.now()}_${Math.random()}`,
                type: newDep.type || 'FS',
                predecessorId: newDep.predecessorId,
                successorId: newDep.successorId,
                projectId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            queryClient.setQueryData<{ dependencies: TaskDependency[]; total: number }>(
                taskDependencyKeys.list(projectId),
                (old) => ({
                    dependencies: [...(old?.dependencies || []), placeholder],
                    total: (old?.total || 0) + 1,
                })
            );
            return { previous };
        },
        onError: (_err, _newDep, context) => {
            if (context?.previous) {
                queryClient.setQueryData(taskDependencyKeys.list(projectId), context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: taskDependencyKeys.list(projectId) });
        },
    });
}

export function useDeleteTaskDependency(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (dependencyId: string) => {
            return apiDelete<{ message: string }>(`/task-dependencies/${dependencyId}`);
        },
        // Optimistic remove so the arrow disappears immediately.
        onMutate: async (dependencyId) => {
            await queryClient.cancelQueries({ queryKey: taskDependencyKeys.list(projectId) });
            const previous = queryClient.getQueryData<{
                dependencies: TaskDependency[];
                total: number;
            }>(taskDependencyKeys.list(projectId));
            queryClient.setQueryData<{ dependencies: TaskDependency[]; total: number }>(
                taskDependencyKeys.list(projectId),
                (old) => ({
                    dependencies: (old?.dependencies || []).filter((d) => d.id !== dependencyId),
                    total: Math.max((old?.total || 0) - 1, 0),
                })
            );
            return { previous };
        },
        // R11 / t39: defensive onSuccess — explicitly filter the
        // dep out of the cache AFTER the DELETE round-trip completes.
        // This guards against any code path that may have
        // re-introduced the dep between the optimistic update and
        // the API success (e.g. an unintended `invalidateQueries`
        // somewhere else, or a race against the WS self-echo refetch).
        // The DELETE has already committed on the backend at this
        // point — the dep is gone for everyone — so it's safe to
        // enforce the local cache reflects that.
        onSuccess: (_data, dependencyId) => {
            queryClient.setQueryData<{ dependencies: TaskDependency[]; total: number }>(
                taskDependencyKeys.list(projectId),
                (old) => ({
                    dependencies: (old?.dependencies || []).filter((d) => d.id !== dependencyId),
                    total: Math.max((old?.total || 0) - 1, 0),
                })
            );
        },
        onError: (_err, _depId, context) => {
            if (context?.previous) {
                queryClient.setQueryData(taskDependencyKeys.list(projectId), context.previous);
            }
        },
        // R9 / t36 + R10 / t38 + R11 / t39: keep the optimistic
        // remove STICKY. The previous behaviour triggered a fresh
        // `invalidateQueries` after the HTTP round-trip. That
        // refetch occasionally raced the originating user's
        // self-echo WS event — the WS handler short-circuits
        // self-echoes, but a refetch from a different code path
        // could still hit the cache first and resurrect the deleted
        // dep for ~1 frame.
        //
        // Correct invariant: once the cache no longer contains the
        // dependency id, we never re-introduce it unless the DELETE
        // fails (in which case `onError` restores the snapshot).
        // `onSuccess` already explicitly filtered the dep out, so
        // by the time `onSettled` runs the cache is correct — we
        // just skip the refetch.
        onSettled: (_data, _error, dependencyId) => {
            const cache = queryClient.getQueryData<{
                dependencies: TaskDependency[];
                total: number;
            }>(taskDependencyKeys.list(projectId));
            const stillInCache = !!cache?.dependencies?.some((d) => d.id === dependencyId);
            if (!stillInCache) return;
            queryClient.invalidateQueries({ queryKey: taskDependencyKeys.list(projectId) });
        },
    });
}

// ============================================
// HELPERS
// ============================================

/**
 * Return the dependency edges where `taskId` is the successor
 * (i.e. other tasks that block this one). Used by the task-detail
 * panel's "Depends on" section.
 */
export function dependenciesWhereTaskIsSuccessor(
    deps: TaskDependency[] | undefined,
    taskId: string
): TaskDependency[] {
    if (!deps) return [];
    return deps.filter((d) => d.successorId === taskId);
}
